import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth/AuthContext";
import { ArrowRight, Building2, Edit, Plus } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import CreateOrganizationForm from "../organizations/CreateOrganizationForm";
import { useIsMobile } from "@/hooks/use-mobile";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const WelcomeBanner = () => {
  const { user, organizations, currentOrganization, refreshOrganizations, isPrimaryFounder } = useAuth();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useIsMobile();

  // Extract first name from user metadata if available
  const firstName = user?.user_metadata?.first_name || "there";

  const handleViewDetails = () => {
    if (currentOrganization) {
      setOrgName(currentOrganization.organization.name);
      setIsDetailsOpen(true);
      setIsEditing(false);
    }
  };

  const handleUpdateOrganization = async () => {
    if (!currentOrganization || !orgName.trim()) return;
    
    try {
      setIsSubmitting(true);
      const { error } = await supabase
        .from('organizations')
        .update({ name: orgName })
        .eq('id', currentOrganization.organization.id);
      
      if (error) throw error;
      
      toast.success('Organization updated successfully');
      await refreshOrganizations();
      setIsEditing(false);
    } catch (error: any) {
      toast.error(`Error updating organization: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const DetailsContent = () => (
    <div className="space-y-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-xl font-semibold">Organization Details</h2>
        </div>
        {isPrimaryFounder() && (
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => setIsEditing(!isEditing)}
            disabled={isSubmitting}
          >
            <Edit className="h-4 w-4 mr-2" />
            {isEditing ? "Cancel" : "Edit"}
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="org-name">Organization Name</Label>
            <Input
              id="org-name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Organization name"
            />
          </div>
          <Button 
            onClick={handleUpdateOrganization} 
            disabled={isSubmitting || !orgName.trim() || orgName === currentOrganization?.organization.name}
          >
            {isSubmitting ? "Updating..." : "Save Changes"}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Organization Name</p>
              <p className="text-base">{currentOrganization?.organization.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Your Role</p>
              <p className="text-base capitalize">{currentOrganization?.role.replace('_', ' ')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="relative mb-8 overflow-hidden rounded-lg border bg-background p-8">
      <div className="relative z-10">
        <h1 className="text-3xl font-bold">Welcome, {firstName}!</h1>
        
        {organizations.length === 0 ? (
          <div className="mt-4 max-w-2xl">
            <p className="text-muted-foreground">
              You haven't created any organizations yet. Create your first organization to get started with Founders CRM.
            </p>
            <Button 
              className="mt-4" 
              onClick={() => setIsCreateDialogOpen(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Organization
            </Button>
          </div>
        ) : (
          <div className="mt-4 max-w-2xl">
            {currentOrganization ? (
              <p className="text-muted-foreground">
                You're currently managing <span className="font-semibold">{currentOrganization.organization.name}</span> as a <span className="capitalize font-semibold">{currentOrganization.role.replace('_', ' ')}</span>.
              </p>
            ) : (
              <p className="text-muted-foreground">
                Please select an organization to manage from the dropdown above.
              </p>
            )}
            <Button 
              className="mt-4" 
              variant="outline" 
              onClick={handleViewDetails}
              disabled={!currentOrganization}
            >
              View Organization Details
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      
      {/* Background decoration */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md">
        <svg
          className="absolute right-0 top-0 h-full w-full text-foreground/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          fill="currentColor"
        >
          <polygon points="0,0 100,0 100,100" />
        </svg>
      </div>
      
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Your First Organization</DialogTitle>
          </DialogHeader>
          <CreateOrganizationForm onSuccess={() => setIsCreateDialogOpen(false)} />
        </DialogContent>
      </Dialog>

      {isMobile ? (
        <Sheet open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Organization Details</SheetTitle>
            </SheetHeader>
            <DetailsContent />
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Organization Details</DialogTitle>
            </DialogHeader>
            <DetailsContent />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default WelcomeBanner;
